import { useToast } from '@act/react-components/app/ReusableToast';
import { CreateMessageRequestDto } from '@act/shared/data-transfer-objects';
import { formatPhoneNumber } from '@act/shared/utilities';
import { FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import CompositeDialog from 'react-components/dialog/CompositeDialog';
import { TrackingProvider } from 'react-components/tracking';
import Loader from '@act/react-components/workflows/Loader';
import { useForm } from 'react-hook-form';
import { membersAppSelectors } from 'redux/slices/memberSlice.slice';
import { createMessageSuccess } from 'redux/slices/messagesSlice.slice';
import { consentRequests } from 'redux/requests/consents.requests';
import { messagesRequests } from 'redux/requests/messages.requests';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { themeColors } from 'toolkit/styles/colors';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import z from 'zod';
import useUnsavedChangesDialog from '../hooks/useUnsavedChangesDialog.hook';
import './text-consents-dialog.scss';
import { useTranslation } from 'react-i18next';

export interface TextConsentsDialogUniqueProps {
  userId: string;
}
export type TextConsentsDialogCallbackInput = void;

export type TextConsentsDialogProps =
  GenericDialogProps<TextConsentsDialogCallbackInput> &
    TextConsentsDialogUniqueProps;

export const ConsentsMessageFormSchema = z.object({
  phoneNumberId: z.number(),
  message: z.string(),
});

type ConsentsMessageForm = z.infer<typeof ConsentsMessageFormSchema>;

export const TextConsentsDialog = ({
  userId,
  open,
  setOpen,
  callback,
}: TextConsentsDialogProps) => {
  const { t } = useTranslation();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const user = membersAppSelectors.useSelectMemberById(userId);
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [linkResponse, isLinkLoading, createConsentLink] = useApiCall(
    consentRequests.createConsentLink
  );
  const [createdMessage, _isCreatingMessage, createMessage] = useApiCall(
    messagesRequests.createMessage
  );
  const linkText = linkResponse?.url;

  const { control, handleSubmit, formState, reset } =
    useForm<ConsentsMessageForm>({
      defaultValues: {
        phoneNumberId: user?.phoneNumbers?.[0]?.id,
        message: t('consentForm.sendSmsMessage', {
          name: user?.name,
        }),
      },
      resolver: zodResolver(ConsentsMessageFormSchema),
    });

  useEffect(() => {
    if (open) {
      createConsentLink({ params: null, body: { userId } });
    }
  }, [open, userId]);

  const handleClose = () => {
    setOpen(false);
    callback();
  };

  const handleSend = async (data: ConsentsMessageForm) => {
    const body: CreateMessageRequestDto = {
      subjectId: userId,
      text: data.message + ' ' + linkText,
      phoneNumberId: data.phoneNumberId,
      fileIds: [],
    };
    try {
      createMessage({ params: null, body });
    } catch (err) {
      console.error(`Error saving message: ${err}`);
    }
  };

  useEffect(() => {
    if (createdMessage) {
      toast('Message has been sent successfully');
      dispatch(createMessageSuccess(createdMessage));
      reset();
      handleClose();
    }
  }, [createdMessage]);

  const formConfig: FormConfig<ConsentsMessageForm> = [
    {
      type: 'input',
      name: 'phoneNumberId',
      label: 'Phone number',
      input: 'select',
      props: {
        options: user?.phoneNumbers?.map(phoneNumber => ({
          label:
            phoneNumber.description +
            ': ' +
            formatPhoneNumber(phoneNumber.number),
          value: phoneNumber.id,
        })),
        defaultValue: user?.phoneNumbers?.[0]?.id,
        fullWidth: true,
        trackingName: 'phone-number-select',
      },
    },
    {
      type: 'input',
      name: 'message',
      label: 'Message',
      input: 'text',
      props: {
        multiline: true,
        minRows: 2,
        verticalAdornments: true,
        trackingName: 'message-text-input',
        iconAdornment: (
          <Box
            sx={{
              backgroundColor: themeColors.inverseOnSurface,
              padding: '8px',
              borderRadius: '4px',
              marginTop: '12px',
            }}
          >
            <Typography variant="bodyMedium">{linkText}</Typography>
          </Box>
        ),
      },
    },
  ];

  const { isDirty } = formState;

  const handleFormClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        handleClose();
      });
    } else {
      handleClose();
    }
  };

  return (
    <TrackingProvider contextName="text-consents-dialog">
      <CompositeDialog
        handleClose={handleFormClose}
        open={open}
        title={`Send consents text to ${user?.name}`}
        handleFormSubmit={handleSubmit(handleSend)}
        actions={[
          {
            text: 'Cancel',
            callback: handleFormClose,
          },
          {
            text: 'Send',
            type: 'submit',
            isPrimary: true,
            disabled: isLinkLoading,
          },
        ]}
      >
        {isLinkLoading ? (
          <Loader />
        ) : (
          <>
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
            />
          </>
        )}
      </CompositeDialog>
    </TrackingProvider>
  );
};
